import { useCameraDrivers } from '../../hooks/useCameraDrivers';
import React, { useState, useEffect } from 'react';
import { cameraApi, companyApi } from '../../services/api';
import { useAppStore } from '../../store/appStore';

// All IANA zones (native, Node/modern-browser). Falls back to a small list if unsupported.
const TZ_OPTIONS = (typeof Intl.supportedValuesOf === 'function')
  ? Intl.supportedValuesOf('timeZone')
  : ['America/Los_Angeles', 'America/Denver', 'America/Chicago', 'America/New_York', 'UTC'];

export default function AddCameraModal({ onClose, onSaved, editCamera }) {
  const drivers = useCameraDrivers();
  const { groups, addCamera, updateCamera, addToast, user } = useAppStore();
  const isEdit = !!editCamera;
  const isAdmin = user?.role === 'admin';

  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    if (isAdmin) {
      companyApi.list().then(res => setCompanies(res.data)).catch(() => {});
    }
  }, [isAdmin]);

  const [form, setForm] = useState(editCamera ? {
    name: editCamera.name,
    location: editCamera.location || '',
    gpsLat: editCamera.gpsLat || '',
    gpsLng: editCamera.gpsLng || '',
    timezone: editCamera.timezone || '',
    ipAddress: editCamera.ipAddress,
    httpPort: editCamera.httpPort || 80,
    rtspPort: editCamera.rtspPort || 554,
    username: editCamera.username || 'root',
    password: editCamera.password || '',
    groupId: editCamera.groupId || '',
    companyId: editCamera.companyId || '',
    tags: (editCamera.tags || []).join(', '),
    streamProfile: editCamera.streamProfile || '',
    manufacturer: editCamera.manufacturer || 'axis',
    rtspUrl: editCamera.rtspUrl || '',
    invertTilt: editCamera.invertTilt || false,
    invertPan: editCamera.invertPan || false,
    invertZoom: editCamera.invertZoom || false,
    dahuaPtzChannel: editCamera.dahuaPtzChannel || '',
    hikvisionPtzChannel: editCamera.hikvisionPtzChannel || '',
  } : {
    name: '', location: '', gpsLat: '', gpsLng: '', timezone: '',
    ipAddress: '', httpPort: 80, rtspPort: 554,
    username: 'root', password: '',
    groupId: '', companyId: '', tags: '', streamProfile: '',
    manufacturer: 'axis', rtspUrl: '',
    invertTilt: false, invertPan: false, invertZoom: false,
    dahuaPtzChannel: '', hikvisionPtzChannel: '',
  });
  // The selected brand's descriptor drives every field below, so this form has no
  // per-brand branches: adding a camera type means adding a driver, not editing this file.
  const driverDesc = drivers?.[form.manufacturer] || drivers?.axis || null;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tzLoading, setTzLoading] = useState(false);

  const update = (field, val) => setForm(f => ({ ...f, [field]: val }));

  async function autoDetectTz() {
    if (!form.gpsLat || !form.gpsLng) return;
    setTzLoading(true);
    try {
      const res = await cameraApi.tzFromGps(form.gpsLat, form.gpsLng);
      if (res.data.timezone) {
        update('timezone', res.data.timezone);
        addToast({ type: 'success', message: `Timezone: ${res.data.timezone}` });
      } else {
        addToast({ type: 'warning', message: 'Could not determine timezone from those coordinates' });
      }
    } catch {
      addToast({ type: 'error', message: 'Timezone lookup failed' });
    } finally {
      setTzLoading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!form.name || !form.ipAddress) { setError('Name and IP Address are required'); return; }

    setLoading(true);
    try {
      const payload = {
        ...form,
        httpPort: parseInt(form.httpPort) || 80,
        rtspPort: parseInt(form.rtspPort) || 554,
        groupId: form.groupId || null,
        companyId: form.companyId || null,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        gpsLat: form.gpsLat ? parseFloat(form.gpsLat) : null,
        gpsLng: form.gpsLng ? parseFloat(form.gpsLng) : null,
        dahuaPtzChannel: form.dahuaPtzChannel ? parseInt(form.dahuaPtzChannel) : null,
        hikvisionPtzChannel: form.hikvisionPtzChannel ? parseInt(form.hikvisionPtzChannel) : null,
      };

      if (isEdit) {
        const res = await cameraApi.update(editCamera.id, payload);
        updateCamera(res.data);
        addToast({ type: 'success', message: `Camera "${form.name}" updated` });
      } else {
        const res = await cameraApi.create(payload);
        addCamera(res.data);
        addToast({ type: 'success', message: `Camera "${form.name}" added` });
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save camera');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: '580px' }}>
        <div className="modal-header">
          <h2>{isEdit ? 'Edit Camera' : 'Add New Camera'}</h2>
          <button className="btn btn-ghost btn-sm btn-icon" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {/* Basic Info */}
            <div style={{ marginBottom: '20px' }}>
              <div className="section-title" style={{ marginBottom: '12px' }}>Basic Information</div>
              <div style={{ marginBottom: '10px' }}>
                <label className="label">Manufacturer</label>
                <select
                  className="input-field select-field"
                  value={form.manufacturer}
                  onChange={e => {
                    const mfr = e.target.value;
                    setForm(f => ({
                      ...f,
                      manufacturer: mfr,
                      username: mfr === 'axis' ? 'root' : 'admin',
                      streamProfile: '',
                      rtspUrl: '',
                    }));
                  }}
                >
                  <option value="axis">Axis</option>
                  <option value="dahua">Dahua</option>
                  <option value="hikvision">Hikvision</option>
                </select>
              </div>
              <div className="grid-2">
                <div>
                  <label className="label">Camera Name *</label>
                  <input className="input-field" placeholder="Front Entrance" value={form.name} onChange={e => update('name', e.target.value)} />
                </div>
                <div>
                  <label className="label">Group</label>
                  <select className="input-field select-field" value={form.groupId} onChange={e => update('groupId', e.target.value)}>
                    <option value="">No Group</option>
                    {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                  </select>
                </div>
              </div>
              {isAdmin && (
                <div style={{ marginTop: '10px' }}>
                  <label className="label">Company <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(restricts visibility to company users)</span></label>
                  <select className="input-field select-field" value={form.companyId} onChange={e => update('companyId', e.target.value)}>
                    <option value="">No Company (admin-only)</option>
                    {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              )}
            </div>

            {/* Location */}
            <div style={{ marginBottom: '20px' }}>
              <div className="section-title" style={{ marginBottom: '12px' }}>Location</div>
              <div style={{ marginBottom: '10px' }}>
                <label className="label">Location Description</label>
                <input className="input-field" placeholder="Building A - 3rd Floor Hallway" value={form.location} onChange={e => update('location', e.target.value)} />
              </div>
              <div className="grid-2">
                <div>
                  <label className="label">GPS Latitude</label>
                  <input className="input-field" placeholder="37.7749" type="number" step="any" value={form.gpsLat} onChange={e => update('gpsLat', e.target.value)} />
                </div>
                <div>
                  <label className="label">GPS Longitude</label>
                  <input className="input-field" placeholder="-122.4194" type="number" step="any" value={form.gpsLng} onChange={e => update('gpsLng', e.target.value)} />
                </div>
              </div>
              <div style={{ marginTop: '10px' }}>
                <label className="label">Timezone</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <select className="input-field select-field" value={form.timezone} onChange={e => update('timezone', e.target.value)} style={{ flex: 1 }}>
                    <option value="">Auto (from GPS)</option>
                    {TZ_OPTIONS.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                  </select>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={autoDetectTz} disabled={tzLoading || !form.gpsLat || !form.gpsLng} title="Detect timezone from GPS coordinates">
                    {tzLoading ? '…' : 'Auto-detect'}
                  </button>
                </div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', color: 'var(--text-muted)', marginTop: '4px' }}>
                  Used by the operator schedule. Left on Auto, it's derived from the GPS coordinates on save.
                </div>
              </div>
            </div>

            {/* Network */}
            <div style={{ marginBottom: '20px' }}>
              <div className="section-title" style={{ marginBottom: '12px' }}>Network Configuration</div>
              <div style={{ marginBottom: '10px' }}>
                <label className="label">Public IP Address *</label>
                <input className="input-field" placeholder="203.0.113.10" value={form.ipAddress} onChange={e => update('ipAddress', e.target.value)} />
              </div>
              <div className="grid-2">
                <div>
                  <label className="label">HTTP Port (WAN)</label>
                  <input className="input-field" type="number" value={form.httpPort} onChange={e => update('httpPort', e.target.value)} />
                </div>
                <div>
                  <label className="label">RTSP Port (WAN)</label>
                  <input className="input-field" type="number" value={form.rtspPort} onChange={e => update('rtspPort', e.target.value)} />
                </div>
              </div>
            </div>

            {/* Auth */}
            <div style={{ marginBottom: '20px' }}>
              <div className="section-title" style={{ marginBottom: '12px' }}>Camera Credentials</div>
              <div className="grid-2">
                <div>
                  <label className="label">Username</label>
                  <input className="input-field" placeholder="root" value={form.username} onChange={e => update('username', e.target.value)} />
                </div>
                <div>
                  <label className="label">Password</label>
                  <input className="input-field" type="password" value={form.password} onChange={e => update('password', e.target.value)} />
                </div>
              </div>
            </div>

            {/* Stream Settings */}
            <div style={{ marginBottom: '20px' }}>
              <div className="section-title" style={{ marginBottom: '12px' }}>Stream Settings</div>
              {/* Whether a brand is configured by stream profile or by RTSP URL, and what a
                  sensible example looks like, is the driver's to describe. */}
              {(() => {
                const st = driverDesc?.config.stream;
                if (!st) return null;
                const isProfile = st.mode === 'profile';
                return (
                  <div>
                    <label className="label">
                      {st.label}{' '}
                      {st.hint && <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>{st.hint}</span>}
                    </label>
                    <input
                      className="input-field"
                      placeholder={st.placeholder}
                      value={isProfile ? form.streamProfile : form.rtspUrl}
                      onChange={e => update(isProfile ? 'streamProfile' : 'rtspUrl', e.target.value)}
                    />
                  </div>
                );
              })()}
            </div>

            {/* PTZ Controls */}
            <div style={{ marginBottom: '20px' }}>
              <div className="section-title" style={{ marginBottom: '12px' }}>PTZ Controls</div>
              {/* Extra settings a brand needs are declared by its driver, so adding a brand
                  does not mean editing this form. */}
              {(driverDesc?.config.fields ?? []).map(f => (
                <div key={f.key} style={{ marginBottom: '10px' }}>
                  <label className="label">
                    {f.label}{' '}
                    {f.hint && <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>{f.hint}</span>}
                  </label>
                  <input
                    className="input-field"
                    type={f.type || 'text'}
                    min={f.min}
                    placeholder={f.placeholder}
                    value={form[f.key] ?? ''}
                    onChange={e => update(f.key, e.target.value)}
                  />
                </div>
              ))}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {driverDesc?.config.showInvertToggles && [
                  { field: 'invertTilt', label: 'Invert Tilt (up/down reversed)' },
                  { field: 'invertPan', label: 'Invert Pan (left/right reversed)' },
                  { field: 'invertZoom', label: 'Invert Zoom (in/out reversed)' },
                ].map(({ field, label }) => (
                  <label key={field} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: '12px', color: 'var(--text-secondary)' }}>
                    <input
                      type="checkbox"
                      checked={form[field]}
                      onChange={e => update(field, e.target.checked)}
                      style={{ width: '14px', height: '14px', cursor: 'pointer' }}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>

            {/* Tags */}
            <div>
              <label className="label">Tags (comma-separated)</label>
              <input className="input-field" placeholder="exterior, main-gate, hd" value={form.tags} onChange={e => update('tags', e.target.value)} />
            </div>

            {error && (
              <div style={{ marginTop: '16px', padding: '10px 12px', background: 'rgba(255,51,68,0.1)', border: '1px solid rgba(255,51,68,0.3)', borderRadius: 'var(--radius-md)', color: 'var(--accent-red)', fontSize: '13px', fontFamily: 'var(--font-mono)' }}>
                {error}
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : isEdit ? 'Save Changes' : 'Add Camera'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
